import { Check, Search, UserX } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Avatar, discordAvatarUrl } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Dialog } from '../../../components/Dialog';
import { Input } from '../../../components/Input';
import { cn } from '../../../lib/cn';
import { useListUsersQuery } from '../../users/users.api';

import { type AssigneeValue } from './AssigneePicker';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (value: AssigneeValue) => void;
    /** null = the "unassigned" option is the current pick; undefined = nothing picked. */
    selectedId?: number | null;
    /** Copy overrides so the same picker can read as a filter ("Ticketele cui?"). */
    title?: string;
    description?: string;
    nullLabel?: string;
};

// Full popup for choosing who handles a ticket. Replaces the in-form dropdown
// (which scrolled a list inside the already-scrolling form dialog). Same clean
// fixed-height pattern as AddReferenceDialog: search pinned on top, one tall
// results list that owns the only scroll. Mobile-first.
export function ColleaguePickerDialog({
    open,
    onOpenChange,
    onSelect,
    selectedId,
    title = 'Cine se ocupa?',
    description = 'Cauta un coleg dupa nume si apasa pe el.',
    nullLabel = 'Lasa neatribuit',
}: Props) {
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!open) {
            setSearchInput('');
            setQuery('');
        }
    }, [open]);

    useEffect(() => {
        const t = window.setTimeout(() => setQuery(searchInput.trim()), 300);
        return () => window.clearTimeout(t);
    }, [searchInput]);

    const { data, isFetching } = useListUsersQuery(
        { search: query || undefined, active: true, limit: 50, minRoleLevel: 2 },
        { skip: !open },
    );

    const colleagues = useMemo(
        () => (data?.data ?? []).filter((u) => (u.role?.level ?? 0) >= 2),
        [data],
    );

    const pick = (value: AssigneeValue) => {
        onSelect(value);
        onOpenChange(false);
    };

    return (
        <Dialog
            open={open}
            closeOnBackdrop={false}
            onOpenChange={onOpenChange}
            maxWidth="max-w-md"
            fixedHeight="h-[min(85vh,38rem)]"
            title={title}
            description={description}
            footer={
                <Button type="button" variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
                    Inchide
                </Button>
            }
        >
            <div className="flex h-full flex-col gap-3">
                <Input
                    type="search"
                    icon={Search}
                    className="h-12 text-base"
                    placeholder="Cauta un coleg dupa nume..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    aria-label="Cauta coleg"
                    autoFocus
                />

                <button
                    type="button"
                    onClick={() => pick(null)}
                    className={cn(
                        'flex shrink-0 items-center gap-3 rounded-xl border border-dashed border-solid px-3 py-2.5 text-left text-sm font-medium transition-colors',
                        // Strict null: `undefined` means "nothing picked yet" (filter
                        // mode) and must not light this option up.
                        selectedId === null
                            ? 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/30 dark:text-blue-200'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800',
                    )}
                >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-400">
                        <UserX aria-hidden="true" className="h-5 w-5" />
                    </span>
                    {nullLabel}
                </button>

                <ul className="v2-scroll mb-2 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-2xl bg-gray-50 p-2 dark:bg-gray-900/40">
                    {isFetching ? (
                        <li className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Se cauta...</li>
                    ) : colleagues.length === 0 ? (
                        <li className="px-3 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                            {query ? 'Niciun coleg gasit.' : 'Niciun coleg.'}
                        </li>
                    ) : (
                        colleagues.map((u) => {
                            const isSel = selectedId === u.id;
                            const label = u.name || u.email;
                            return (
                                <li key={u.id}>
                                    <button
                                        type="button"
                                        onClick={() => pick({ assigneeUserId: u.id, assigneeName: label })}
                                        className={cn(
                                            'flex w-full items-center gap-3 rounded-xl border border-solid px-3 py-2.5 text-left transition-all',
                                            isSel
                                                ? 'border-blue-500 bg-blue-50 ring-1 ring-inset ring-blue-500 dark:border-blue-500 dark:bg-blue-950/40'
                                                : 'border-gray-200 bg-white shadow-sm hover:border-blue-300 hover:shadow dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-700',
                                        )}
                                    >
                                        <Avatar
                                            src={discordAvatarUrl(u.discordId, u.discordAvatar)}
                                            name={label}
                                            seed={u.id}
                                            className="h-10 w-10 text-sm"
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-semibold text-gray-900 dark:text-gray-100">
                                                {label}
                                            </span>
                                            {u.name && (
                                                <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                                    {u.email}
                                                </span>
                                            )}
                                        </span>
                                        {isSel && (
                                            <Check aria-hidden="true" className="ml-1 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                                        )}
                                    </button>
                                </li>
                            );
                        })
                    )}
                </ul>
            </div>
        </Dialog>
    );
}
